/**
 * organization-plate — the open organization's monogram, and the one trigger
 * for `<organization-chooser>`. A button, not a navigation-rail-item, so in a
 * rail it stays out of the destinations' roving tabindex, announces a popup
 * and never latches selected. The monogram alone: the whole name is the
 * tooltip, the accessible name and the chooser's head.
 *
 * Data down, intent up. The host sets the organizations, which one is open,
 * the switch's and the sign-out's progress, the signed-in address and how
 * many invitations are waiting — a point mark on the plate and a suffix on
 * its accessible name, since the chooser is where they are answered; the
 * plate hands the rest on to the chooser it opens and re-dispatches the
 * chooser's intents from itself, bubbling and composed:
 *   organization-select  detail: the organization id picked
 *   sign-out             the person confirmed signing out
 *   chooser-close        the chooser closed; refusals it showed are spent
 *
 * After a landed switch the host sets `focusPlateOnMount`; the plate marked
 * `returnsFocus` that connects while it is set takes the focus.
 */
import { tooltip } from "@mhmo91/schmancy";
import { art, reducedMotion$ } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { openOverlays$, show } from "@mhmo91/schmancy/overlay";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import {
	endWith,
	exhaustMap,
	filter,
	finalize,
	from,
	fromEvent,
	ignoreElements,
	merge,
	Subject,
	share,
	switchMap,
	take,
	takeUntil,
	tap,
	timer,
} from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import "./organization-chooser.view.ts";
import type { OrganizationChooser } from "./organization-chooser.view.ts";
import {
	focusPlateOnMount,
	organizationInitials,
} from "./organization-plate.state.ts";

@schmancyElement("organization-plate")
export class OrganizationPlate extends LocalizeMixin(SchmancyElement) {
	// The plate is the rail's last row and the sheet header's trailing item: a
	// block host would put its inline-flex button on a text line and grow it.
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
			}
		`,
	];

	@property({ attribute: false }) organizations: readonly {
		readonly id: string;
		readonly name: string;
	}[] = [];
	@property({ attribute: false }) organizationId: string | null = null;
	@property({ attribute: false }) status: "loading" | "ready" | "error" =
		"ready";
	@property({ type: Boolean }) switching = false;
	@property() error = "";
	@property() email = "";
	@property({ type: Boolean }) signingOut = false;
	@property({ type: Boolean }) signOutFailed = false;
	/** Open invitations waiting in the chooser. */
	@property({ type: Number }) invitations = 0;
	// The one plate on a surface that takes the focus a landed switch owes.
	@property({ type: Boolean }) returnsFocus = false;

	// The open chooser, so a change of the host's data reaches it in place.
	private chooser: OrganizationChooser | null = null;
	private readonly openChooser$ = new Subject<MouseEvent>();

	override connectedCallback(): void {
		super.connectedCallback();
		const phone = window.matchMedia("(max-width: 767.98px)");

		merge(
			// show() anchors a panel's corner on the gesture's point — where the
			// plate was pressed, or its centre for a keyboard press. The offset is
			// that point's distance to the plate's right edge plus the overlay's
			// own 8px gap, so a panel placed right-start opens clear of it. A phone
			// has no room beside the plate, so there show() lays the panel out.
			this.openChooser$.pipe(
				exhaustMap((e) => {
					const control =
						e.currentTarget instanceof HTMLElement ? e.currentTarget : null;
					const box = control?.getBoundingClientRect();
					const label = this.t(
						this.organizations.length > 1
							? "admin.nav.switchOrganization"
							: "admin.nav.accountAndSignOut",
						{
							name:
								this.organizations.find((org) => org.id === this.organizationId)
									?.name ?? "",
						},
					);
					const chooser = document.createElement("organization-chooser");
					this.chooser = chooser;
					this.requestUpdate();
					const shown$ = show(
						chooser,
						phone.matches || box === undefined
							? { ariaLabel: label }
							: {
									ariaLabel: label,
									preferredPlacement: "right-start",
									offset:
										box.right -
										(e.detail > 0 ? e.clientX : box.left + box.width / 2) +
										8,
									grow: false,
								},
					).pipe(share());
					return merge(
						shown$.pipe(ignoreElements()),
						// The chooser's intents leave from this plate, for as long as its
						// panel is open: show() completing is the close.
						merge(
							fromEvent<CustomEvent<string>>(chooser, "organization-select"),
							fromEvent<CustomEvent<undefined>>(chooser, "sign-out"),
						).pipe(
							tap((intent) =>
								this.dispatchEvent(
									new CustomEvent(intent.type, {
										detail: intent.detail,
										bubbles: true,
										composed: true,
									}),
								),
							),
							takeUntil(shown$.pipe(ignoreElements(), endWith(null))),
						),
					).pipe(
						finalize(() => {
							this.chooser = null;
							this.dispatchEvent(
								new CustomEvent("chooser-close", {
									bubbles: true,
									composed: true,
								}),
							);
						}),
					);
				}),
			),

			// Read once, at connect: only a plate that mount brought takes the
			// focus owed, once every overlay has closed.
			from(this.updateComplete).pipe(
				filter(() => this.returnsFocus && focusPlateOnMount.value),
				tap(() => focusPlateOnMount.set(false)),
				switchMap(() =>
					openOverlays$.pipe(
						filter((stack) => stack.length === 0),
						take(1),
					),
				),
				switchMap(() => timer(0)),
				tap(() =>
					this.renderRoot
						.querySelector<HTMLElement>("schmancy-button")
						?.focus(),
				),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override updated() {
		if (this.chooser)
			Object.assign(this.chooser, {
				organizations: this.organizations,
				organizationId: this.organizationId,
				status: this.status,
				switching: this.switching,
				error: this.error,
				email: this.email,
				signingOut: this.signingOut,
				signOutFailed: this.signOutFailed,
			});
	}

	override render() {
		const name =
			this.organizations.find((org) => org.id === this.organizationId)?.name ??
			"";
		// Outlined, because an identity-tinted fill can land on a lit rail row's
		// own primary-container and read as that row. The monogram box is
		// absolutely placed because a plain button underlines its content on
		// hover and focus, and an in-flow descendant cannot cancel a decoration
		// it inherits.
		return html`
			<schmancy-button
				variant="plain"
				color="neutral"
				${tooltip(name, { position: "right" })}
				.ariaLabel=${`${this.t(
					// With one organization nothing switches: the chooser opens only
					// on the account and sign-out.
					this.organizations.length > 1
						? "admin.nav.switchOrganization"
						: "admin.nav.accountAndSignOut",
					{ name },
				)}${
					this.invitations > 0
						? `, ${this.t(
								this.invitations === 1
									? "admin.nav.invitationsWaitingOne"
									: "admin.nav.invitationsWaitingOther",
								{ count: this.invitations },
							)}`
						: ""
				}`}
				aria-haspopup="menu"
				class="rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default"
				@click=${(e: MouseEvent) => this.openChooser$.next(e)}
			>
				<span class="relative block size-12">
					<span
						class="absolute inset-0.5 grid place-items-center rounded-xl transition-colors hover:bg-surface-containerHighest"
						${art({ name: "facade", color: "var(--schmancy-sys-color-primary-default)", intensity: 1, speed: 1, active: !reducedMotion$.value })}
					>
						<schmancy-avatar
							aria-hidden="true"
							color="transparent"
							bordered
							shape="square"
							class="w-10 h-10"
							initials=${organizationInitials(name)}
						></schmancy-avatar>
					</span>
					${when(
						this.invitations > 0,
						// Decorative: the button's own name already says what the mark
						// means. The ring is the host's, in the rail's own ground.
						() => html`
							<schmancy-tone-dot
								data-invitations
								aria-hidden="true"
								color="primary"
								class="absolute right-0 top-0 size-3 ring-2 ring-surface-containerLow"
							></schmancy-tone-dot>
						`,
					)}
				</span>
			</schmancy-button>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"organization-plate": OrganizationPlate;
	}
}
